import { MenuOutlined } from '@ant-design/icons'
import {
  Button,
  Checkbox,
  Flex,
  Popover,
  Spin,
  Table,
} from 'antd'
import React, {
  useMemo,
  useState,
} from 'react'
import useAsync from 'react-use/lib/useAsync'

import { EarningType } from '@/constants/earning-type'
import { FeatureKey } from '@/constants/feature-key'
import { readCreatorOverviewStatistics } from '@/redux/creator-reports'
import {
  useAppDispatch,
  useAppSelector,
} from '@/redux/utils'

import { ReportsPageProps } from '../../hooks/use-report-search'
import useStatisticColumns from '../../hooks/use-statistic-columns'
import SectionTitle from '../section-title'
import StatisticNumber from '../statistic-number'

export default function CreatorOverviewStatistics({ searchValues }: ReportsPageProps) {
  const { earningType = EarningType.GrossEarnings } = searchValues
  const { creatorOverviewStatistics } = useAppSelector(state => state.creatorReports)
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(false)
  const { startAt, endAt, period, creatorIds } = searchValues

  const columns = useStatisticColumns()
  const [columnCheckedList, setColumnCheckedList] = useState<string[]>(
    columns
      .filter(column => column.defaultColumnChecked)
      .map(column => column.key),
  )

  const filteredColumns = useMemo(() =>
    columns.filter(column => column.columnChecked || columnCheckedList.includes(column.key),
    ), [columnCheckedList, columns])

  useAsync(async () => {
    setLoading(true)
    await dispatch(readCreatorOverviewStatistics({
      startAt,
      endAt,
      period,
      creatorIds,
      permissionKey: FeatureKey.AnalyticsCreatorOverview,
    }))
    setLoading(false)
  }, [dispatch, startAt, endAt, period, creatorIds])

  const statistics = creatorOverviewStatistics?.[earningType]

  return (
    <Spin spinning={loading}>
      <Flex vertical gap={16}>
        <Flex justify="space-between">
          <SectionTitle title="Creator statistics" />
          <Popover content={
            <Checkbox.Group
              defaultValue={columnCheckedList}
              onChange={setColumnCheckedList}
            >
              <Flex vertical>
                {columns.slice(1).map(column => (
                  <Checkbox
                    style={{ padding: 8 }}
                    key={column.key}
                    value={column.key}
                  >
                    {column.title}
                  </Checkbox>
                ))}
              </Flex>
            </Checkbox.Group>
          } trigger="click">
            <Button size="small" type="text" icon={<MenuOutlined />}>Custom metrics</Button>
          </Popover>
        </Flex>
        <Flex gap={16}>
          <StatisticNumber
            title="Creators" value={statistics?.creators.length}
            tooltip="The total Creators that your team manages"
            precision={0}
          />
          <StatisticNumber
            title="Message earnings"
            value={statistics?.messageEarnings}
            tooltip="Total message earnings from managed Creators"
            prefix="🌟"
          />
          <StatisticNumber
            title="Total earnings"
            value={statistics?.totalEarnings}
            tooltip="A sum of Creator earnings from all channels"
            prefix="🌟"
          />
          <StatisticNumber
            title="Refunded"
            value={statistics?.refunded ?? 0}
            tooltip="Earnings that were refunded during this period"
            prefix="🌟"
          />
        </Flex>
        <Flex vertical style={{ marginBottom: 24 }}>
          <Table
            columns={filteredColumns}
            rowKey="id"
            dataSource={statistics?.creators}
            pagination={false}
          />
        </Flex>
      </Flex>
    </Spin>
  )
}
